import { useMemo, useState } from "react";
import { Eye, EyeOff, Loader2, UserPlus, X } from "lucide-react";
import type { Role, User } from "../../types";
import { apiService } from "../../utils/apiService";
import {
  ALL_ROLES,
  ROLE_LABELS,
  userHasRole,
} from "../../utils/accessControl";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { formatPhilippinePhoneInput, normalizePhilippinePhone } from "../../utils/phoneNumber";

interface AddUserDialogProps {
  currentUser: User;
  onClose: () => void;
  onCreated: (user: User) => void;
}

const initialForm = {
  name: "",
  email: "",
  phone: "",
  school: "",
  password: "",
  roles: ["fabricator"] as Role[],
};

export function AddUserDialog({
  currentUser,
  onClose,
  onCreated,
}: AddUserDialogProps) {
  const [form, setForm] = useState(initialForm);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const canAssignPrivileged = userHasRole(currentUser, "sysadmin");

  const passwordValid = useMemo(
    () =>
      form.password.length >= 12 &&
      /[a-z]/.test(form.password) &&
      /[A-Z]/.test(form.password) &&
      /\d/.test(form.password) &&
      /[^A-Za-z0-9]/.test(form.password),
    [form.password],
  );

  const canSubmit =
    form.name.trim() !== "" &&
    /^\S+@\S+\.\S+$/.test(form.email) &&
    passwordValid &&
    form.roles.length > 0 &&
    !isSubmitting;

  const toggleRole = (role: Role, checked: boolean) => {
    setForm((current) => ({
      ...current,
      roles: checked
        ? [...new Set([...current.roles, role])]
        : current.roles.filter((item) => item !== role),
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError("");
    try {
      const phone = normalizePhilippinePhone(form.phone);
      const response = await apiService.createUser({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        phone,
        school: form.school.trim() || undefined,
        roles: form.roles,
      });
      if (!response.data?.user) throw new Error("The server did not return the new user.");
      onCreated(response.data.user);
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Unable to create the account.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-user-title"
        className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-border bg-background shadow-xl"
      >
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-border bg-background px-5 py-4 sm:px-6">
          <div>
            <h2 id="add-user-title" className="text-xl font-semibold">
              Add new user
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Create the login and assign its initial access roles.
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 p-5 sm:p-6">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="new-user-name">Full name</Label>
              <Input
                id="new-user-name"
                autoFocus
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                placeholder="Juan Dela Cruz"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-user-email">Email address</Label>
              <Input
                id="new-user-email"
                type="email"
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
                placeholder="member@example.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-user-phone">Phone number</Label>
              <Input
                id="new-user-phone"
                value={form.phone}
                onChange={(event) => setForm({ ...form, phone: formatPhilippinePhoneInput(event.target.value) })}
                placeholder="+639XXXXXXXXX"
                inputMode="tel"
                maxLength={13}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-user-school">Department or institution</Label>
              <Input
                id="new-user-school"
                value={form.school}
                onChange={(event) => setForm({ ...form, school: event.target.value })}
                placeholder="Engineering"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="new-user-password">Initial password</Label>
              <div className="relative">
                <Input
                  id="new-user-password"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(event) => setForm({ ...form, password: event.target.value })}
                  className="pr-11"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className={form.password && !passwordValid ? "text-xs text-destructive" : "text-xs text-muted-foreground"}>
                Use 12 or more characters with uppercase, lowercase, number, and symbol.
              </p>
            </div>
          </div>

          <fieldset className="space-y-3">
            <legend className="text-sm font-medium">Roles</legend>
            <div className="grid gap-3 rounded-lg border border-border p-4 sm:grid-cols-2 lg:grid-cols-3">
              {ALL_ROLES.map((role) => {
                const privileged = role === "sysadmin" || role === "executive";
                return (
                  <label key={role} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.roles.includes(role)}
                      disabled={privileged && !canAssignPrivileged}
                      onCheckedChange={(value) => toggleRole(role, value === true)}
                    />
                    {ROLE_LABELS[role]}
                  </label>
                );
              })}
            </div>
            {!canAssignPrivileged && (
              <p className="text-xs text-muted-foreground">
                Only a System Administrator can assign System Administrator and Executive roles.
              </p>
            )}
          </fieldset>

          {error && (
            <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 border-t border-border pt-5 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              {isSubmitting ? "Creating user..." : "Create user"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
